import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Plus, Pencil, Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import ImageUpload from '@/components/ImageUpload';
import { SHOP_CATEGORIES } from '@/pages/Loja';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const EMPTY = {
  name: '', short_description: '', image_url: '', category: 'lingerie', purchase_url: '',
  free_benefit_label: '', free_benefit_code: '', premium_benefit_label: '', premium_benefit_code: '',
  premium_purchase_url: '', is_published: false, display_order: 0,
};
const FIELDS = Object.keys(EMPTY);

const Field = ({ id, label, hint, form, set, textarea, type = 'text' }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {textarea
      ? <Textarea id={id} rows={3} value={form[id]} onChange={(e) => set(id, e.target.value)} data-testid={`shop-form-${id}`} />
      : <Input id={id} type={type} value={form[id]} onChange={(e) => set(id, type === 'number' ? Number(e.target.value) : e.target.value)} data-testid={`shop-form-${id}`} />}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const ProductForm = ({ initial, onSaved, onClose }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    const body = Object.fromEntries(FIELDS.map((k) => [k, form[k]]));
    try {
      if (form.id) await api.put(`/admin/shop/products/${form.id}`, body);
      else await api.post('/admin/shop/products', body);
      toast.success('Produto guardado');
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || 'Erro ao guardar');
    } finally { setSaving(false); }
  };
  const p = { form, set };
  return (
    <div className="space-y-4">
      <Field id="name" label="Nome *" {...p} />
      <Field id="short_description" label="Descrição curta" textarea {...p} />
      <ImageUpload value={form.image_url} onChange={(v) => set('image_url', v)} label="Imagem do produto" aspectRatio="aspect-[4/5]" recommendedSize="1000×1250 px · 4:5" />
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Categoria *</Label>
          <Select value={form.category} onValueChange={(v) => set('category', v)}>
            <SelectTrigger data-testid="shop-form-category"><SelectValue /></SelectTrigger>
            <SelectContent>{SHOP_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Field id="display_order" label="Ordem" type="number" hint="Menor aparece primeiro" {...p} />
      </div>
      <Field id="purchase_url" label="Link de compra (loja parceira) *" hint="https://..." {...p} />
      <div className="rounded-lg border border-border p-4 space-y-3">
        <p className="text-sm font-medium">Benefício Membros (Free)</p>
        <Field id="free_benefit_label" label="Texto público" hint="Ex.: 5% de desconto. Não escrevas o código aqui." {...p} />
        <Field id="free_benefit_code" label="Código (só membros com sessão)" {...p} />
      </div>
      <div className="rounded-lg border border-gold/40 p-4 space-y-3">
        <p className="text-sm font-medium text-gold">Benefício Premium</p>
        <Field id="premium_benefit_label" label="Texto público" hint="Ex.: 15% de desconto + oferta. Não escrevas o código aqui." {...p} />
        <Field id="premium_benefit_code" label="Código (só Premium)" {...p} />
        <Field id="premium_purchase_url" label="Link de compra Premium (opcional, só Premium)" {...p} />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={form.is_published} onCheckedChange={(v) => set('is_published', v)} data-testid="shop-form-published" />
        <Label>Publicado</Label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} disabled={saving} className="bg-ruby hover:bg-ruby-light" data-testid="shop-form-save">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
        </Button>
      </div>
    </div>
  );
};

export default function AdminShop() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/admin/shop/products').then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    if (!user?.is_admin) { navigate('/'); return; }
    load();
  }, [user, navigate]);

  const togglePublish = async (p) => {
    const body = Object.fromEntries(FIELDS.map((k) => [k, p[k] ?? EMPTY[k]]));
    await api.put(`/admin/shop/products/${p.id}`, { ...body, is_published: !p.is_published });
    load();
  };
  const remove = async (p) => {
    if (!window.confirm(`Apagar "${p.name}"?`)) return;
    await api.delete(`/admin/shop/products/${p.id}`);
    toast.success('Produto apagado');
    load();
  };

  if (!user?.is_admin) return null;
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center"><ShoppingBag className="w-6 h-6 text-ruby" /></div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">Gestão da Loja</h1>
                <p className="text-muted-foreground">Produtos dos parceiros apresentados em /loja</p>
              </div>
            </div>
            <Button onClick={() => setEditing(EMPTY)} className="bg-ruby hover:bg-ruby-light" data-testid="shop-new-product">
              <Plus className="w-4 h-4 mr-2" />Novo produto
            </Button>
          </div>
          {!items ? <Loader2 className="w-8 h-8 animate-spin text-ruby mx-auto" /> : items.length === 0 ? (
            <p className="text-muted-foreground py-12 text-center" data-testid="shop-admin-empty">Ainda não há produtos.</p>
          ) : (
            <div className="space-y-3" data-testid="shop-admin-list">
              {items.map((p) => (
                <div key={p.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-3" data-testid={`shop-admin-item-${p.id}`}>
                  <div className="h-16 w-14 shrink-0 overflow-hidden rounded-md bg-white/5">
                    {p.image_url && <img src={p.image_url} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">{p.name}</p>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      <Badge variant="outline">{SHOP_CATEGORIES.find((c) => c.value === p.category)?.label}</Badge>
                      <Badge variant="outline">Ordem {p.display_order}</Badge>
                      <Badge className={p.is_published ? 'bg-green-600/20 text-green-400 border-0' : 'bg-white/10 text-muted-foreground border-0'}>{p.is_published ? 'Publicado' : 'Rascunho'}</Badge>
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => togglePublish(p)} title={p.is_published ? 'Despublicar' : 'Publicar'} data-testid={`shop-admin-toggle-${p.id}`}>
                    {p.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`shop-admin-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-destructive" data-testid={`shop-admin-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="shop-form-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Editar produto' : 'Novo produto'}</DialogTitle></DialogHeader>
          {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
